const TERMINAL_STATUSES = new Set(['cancelled', 'completed', 'no show']);
const PAYMENT_HOLD_STATUSES = new Set(['pending_payment', 'processing']);

export const parseAppointmentDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** New bookings are always created as confirmed on the calendar. */
export const getDefaultAppointmentStatus = () => 'confirmed';

export const isAwaitingClientConfirmation = (appointment) => {
  if (!appointment || appointment.clientConfirmed === true) return false;
  const status = String(appointment.status || '').toLowerCase();
  if (TERMINAL_STATUSES.has(status) || PAYMENT_HOLD_STATUSES.has(status)) return false;
  return status === 'confirmed' || status === 'pending';
};

export const isCheckedIn = (appointment) => Boolean(appointment?.checkedInAt);

export const getCheckInSettings = (bookingSettings = {}) => ({
  allowClientCheckIn: bookingSettings.allowClientCheckIn === true,
  checkInWindowMinutesBefore: Number(bookingSettings.checkInWindowMinutesBefore) > 0
    ? Number(bookingSettings.checkInWindowMinutesBefore)
    : 30,
  requireClientConfirmedBeforeCheckIn: bookingSettings.requireClientConfirmedBeforeCheckIn === true,
  automatedCheckInReminderEnabled: bookingSettings.automatedCheckInReminderEnabled === true,
  checkInReminderSendSms: bookingSettings.checkInReminderSendSms === true,
  checkInReminderSendEmail: bookingSettings.checkInReminderSendEmail === true,
});

export const canSendCheckInLink = (appointment, bookingSettings = {}) => {
  const settings = getCheckInSettings(bookingSettings);

  if (!appointment) {
    return { eligible: false, reason: 'Appointment not found.' };
  }

  if (isCheckedIn(appointment)) {
    return { eligible: false, reason: 'Already checked in.' };
  }

  if (!settings.allowClientCheckIn) {
    return { eligible: false, reason: 'Self check-in is not enabled.' };
  }

  const status = String(appointment.status || '').toLowerCase();
  if (TERMINAL_STATUSES.has(status) || PAYMENT_HOLD_STATUSES.has(status)) {
    return { eligible: false, reason: 'Check-in link cannot be sent for this appointment.' };
  }

  if (status !== 'confirmed' && status !== 'pending' && status !== 'rescheduled') {
    return { eligible: false, reason: 'Check-in link is not available for this appointment.' };
  }

  return { eligible: true, reason: null };
};

export const canCheckInAppointment = (appointment, bookingSettings = {}, options = {}) => {
  const { actor = 'employee' } = options;
  const settings = getCheckInSettings(bookingSettings);

  if (!appointment) {
    return { eligible: false, reason: 'Appointment not found.' };
  }

  if (isCheckedIn(appointment)) {
    return { eligible: false, reason: 'Already checked in.' };
  }

  const status = String(appointment.status || '').toLowerCase();
  if (TERMINAL_STATUSES.has(status)) {
    return { eligible: false, reason: 'This appointment is no longer active.' };
  }
  if (PAYMENT_HOLD_STATUSES.has(status)) {
    return { eligible: false, reason: 'Complete payment before checking in.' };
  }
  if (status !== 'confirmed' && status !== 'pending' && status !== 'rescheduled') {
    return { eligible: false, reason: 'This appointment cannot be checked in yet.' };
  }

  if (actor === 'client' && !settings.allowClientCheckIn) {
    return { eligible: false, reason: 'Self check-in is not enabled for this business.' };
  }

  if (settings.requireClientConfirmedBeforeCheckIn && appointment.clientConfirmed !== true) {
    return { eligible: false, reason: 'Please confirm your appointment before checking in.' };
  }

  const appointmentDate = parseAppointmentDate(appointment.dateTime);
  if (!appointmentDate) {
    return { eligible: false, reason: 'Appointment time is missing.' };
  }

  const now = Date.now();
  const windowStart = appointmentDate.getTime() - settings.checkInWindowMinutesBefore * 60 * 1000;
  const windowEnd = new Date(appointmentDate);
  windowEnd.setHours(23, 59, 59, 999);

  if (now < windowStart) {
    return { eligible: false, reason: 'Check-in is not open yet.' };
  }
  if (now > windowEnd.getTime()) {
    return { eligible: false, reason: 'Check-in window has closed for today.' };
  }

  return { eligible: true, reason: null };
};

/** Appointments that need staff attention (legacy pending approval or client has not confirmed). */
export const needsAttention = (appointment) => {
  if (!appointment || appointment.archived) return false;

  const status = String(appointment.status || '').toLowerCase();
  if (TERMINAL_STATUSES.has(status) || PAYMENT_HOLD_STATUSES.has(status)) {
    return false;
  }

  if (isAwaitingClientConfirmation(appointment)) return true;
  return status === 'pending';
};

export const isUpcomingAttentionNeeded = (appointment) => {
  if (!needsAttention(appointment)) return false;
  const appointmentDate = parseAppointmentDate(appointment.dateTime);
  if (!appointmentDate) return false;
  return appointmentDate > new Date();
};

/** User-facing status label and color key for chips/badges. */
export const getAppointmentDisplayMeta = (appointment) => {
  if (!appointment) {
    return { label: 'Unknown', colorKey: 'default', cssClass: 'default' };
  }

  if (isCheckedIn(appointment)) {
    return { label: 'Checked In', colorKey: 'checked_in', cssClass: 'checked-in' };
  }

  if (isAwaitingClientConfirmation(appointment)) {
    return {
      label: 'Confirm attendance',
      colorKey: 'pending',
      cssClass: 'awaiting-confirmation',
    };
  }

  const status = String(appointment.status || '').toLowerCase();

  if (status === 'pending_payment') {
    return { label: 'Awaiting Payment', colorKey: 'pending_payment', cssClass: 'pending-payment' };
  }

  if (status === 'pending') {
    return { label: 'Pending approval', colorKey: 'pending', cssClass: 'pending' };
  }

  if (status === 'no show' || status === 'no-show') {
    return { label: 'No Show', colorKey: 'no show', cssClass: 'no-show' };
  }

  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : 'Scheduled';

  return {
    label,
    colorKey: status || 'default',
    cssClass: status.replace(/\s+/g, '-') || 'default',
  };
};
